import { useState } from 'react';
import { ChevronUp, ChevronDown, Info, TrendingUp, Database, Calculator } from 'lucide-react';
import { useSubmarketStore } from '../../store/useSubmarketStore';
import { useActiveMarket } from '../../store/useMarketStore';
import { GoldBadge } from '../Luxury';

type TabType = 'overview' | 'components' | 'data-sources';

const componentData = [
  {
    name: 'Rent Gap',
    weight: '25%',
    correlation: '+0.329',
    description: 'Submarkets with EGI below metro average have room for rent growth',
  },
  {
    name: 'MOBI Index',
    weight: '15%',
    correlation: '+0.312',
    description: 'Low ownership barriers indicate strong rental demand',
  },
  {
    name: 'Momentum',
    weight: '15%',
    correlation: '+0.154',
    description: 'Market cycle position and trajectory',
  },
  {
    name: 'Vacancy',
    weight: '15%',
    correlation: '-0.168',
    description: 'Low vacancy indicates strong absorption',
  },
  {
    name: 'Valuation',
    weight: '10%',
    correlation: '+0.345',
    description: 'Price momentum relative to rent growth',
  },
  {
    name: 'Permits',
    weight: '10%',
    correlation: 'N/A',
    description: 'Development activity as supply indicator',
  },
  {
    name: 'Spillover',
    weight: '10%',
    correlation: 'N/A',
    description: 'Adjacent submarket momentum effects',
  },
];

function getDataSources(permitSourceName: string) {
  return [
    {
      name: "Zandi/Moody's CRE Index",
      icon: TrendingUp,
      description:
        'Commercial real estate price indices and market cycle indicators from Moody\'s Analytics, providing institutional-grade valuation benchmarks.',
    },
    {
      name: 'Census ACS',
      icon: Database,
      description:
        'American Community Survey demographic data including income, homeownership rates, and population trends at the census tract level.',
    },
    {
      name: permitSourceName,
      icon: Calculator,
      description:
        `Permits data from ${permitSourceName} tracking new construction and renovation activity.`,
    },
    {
      name: 'Spillover Model',
      icon: TrendingUp,
      description:
        'Proprietary spatial autocorrelation model measuring momentum transfer between adjacent submarkets.',
    },
  ];
}

export function MethodologySection() {
  const { isMethodologyExpanded, setMethodologyExpanded } = useSubmarketStore();
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const tabs: { key: TabType; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'components', label: 'Components' },
    { key: 'data-sources', label: 'Data Sources' },
  ];

  return (
    <div className="bg-charcoal border-t border-slate-dark">
      {/* Toggle Bar */}
      <button
        onClick={() => setMethodologyExpanded(!isMethodologyExpanded)}
        className="w-full px-6 py-3 flex items-center justify-between hover:bg-graphite/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Info className="w-4 h-4 text-gold" />
          <span className="text-ivory font-medium">Backtesting Methodology</span>
        </div>
        <div className="flex items-center gap-3">
          <GoldBadge variant="gold">+0.438 Correlation</GoldBadge>
          {isMethodologyExpanded ? (
            <ChevronDown className="w-5 h-5 text-stone-500" />
          ) : (
            <ChevronUp className="w-5 h-5 text-stone-500" />
          )}
        </div>
      </button>

      {/* Expandable Content */}
      {isMethodologyExpanded && (
        <div className="border-t border-slate-dark">
          {/* Tab Navigation */}
          <div className="flex border-b border-slate-dark">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === tab.key
                    ? 'text-gold'
                    : 'text-stone-500 hover:text-stone-700'
                }`}
              >
                {tab.label}
                {activeTab === tab.key && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold" />
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && <OverviewTab />}
            {activeTab === 'components' && <ComponentsTab />}
            {activeTab === 'data-sources' && <DataSourcesTab />}
          </div>
        </div>
      )}
    </div>
  );
}

function OverviewTab() {
  const market = useActiveMarket();
  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-graphite border border-slate-dark p-4 text-center">
          <div className="text-2xl font-bold text-emerald font-mono">+0.438</div>
          <div className="text-xs text-stone-500 uppercase tracking-wider mt-1">
            Correlation
          </div>
        </div>
        <div className="bg-graphite border border-slate-dark p-4 text-center">
          <div className="text-2xl font-bold text-ivory font-mono">174</div>
          <div className="text-xs text-stone-500 uppercase tracking-wider mt-1">
            Observations
          </div>
        </div>
        <div className="bg-graphite border border-slate-dark p-4 text-center">
          <div className="text-2xl font-bold text-ivory font-mono">{market.submarket_count}</div>
          <div className="text-xs text-stone-500 uppercase tracking-wider mt-1">
            Submarkets
          </div>
        </div>
      </div>

      {/* Explanation */}
      <div className="text-stone-600 text-sm leading-relaxed">
        <p>
          Our composite submarket score demonstrates a <span className="text-emerald font-semibold">+0.438 correlation</span> with
          5-year total returns across 174 quarterly observations spanning {market.submarket_count} {market.display_name} submarkets.
          This statistically significant relationship (p &lt; 0.001) validates the predictive power of our
          multi-factor scoring methodology for identifying high-performing investment submarkets.
        </p>
      </div>
    </div>
  );
}

function ComponentsTab() {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-dark">
            <th className="text-left py-3 px-4 text-stone-500 font-medium uppercase text-xs tracking-wider">
              Component
            </th>
            <th className="text-center py-3 px-4 text-stone-500 font-medium uppercase text-xs tracking-wider">
              Weight
            </th>
            <th className="text-center py-3 px-4 text-stone-500 font-medium uppercase text-xs tracking-wider">
              Correlation
            </th>
            <th className="text-left py-3 px-4 text-stone-500 font-medium uppercase text-xs tracking-wider">
              Description
            </th>
          </tr>
        </thead>
        <tbody>
          {componentData.map((component, index) => (
            <tr
              key={component.name}
              className={`border-b border-slate-dark/50 hover:bg-graphite/50 transition-colors ${
                index % 2 === 0 ? 'bg-graphite/20' : ''
              }`}
            >
              <td className="py-3 px-4 text-ivory font-medium">{component.name}</td>
              <td className="py-3 px-4 text-center text-gold font-mono">{component.weight}</td>
              <td className="py-3 px-4 text-center">
                <span
                  className={`font-mono ${
                    component.correlation === 'N/A'
                      ? 'text-stone-400'
                      : component.correlation.startsWith('+')
                      ? 'text-emerald'
                      : 'text-crimson'
                  }`}
                >
                  {component.correlation}
                </span>
              </td>
              <td className="py-3 px-4 text-stone-500">{component.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DataSourcesTab() {
  const market = useActiveMarket();
  const dataSources = getDataSources(market.permit_source_name);
  return (
    <div className="grid grid-cols-2 gap-4">
      {dataSources.map((source) => {
        const IconComponent = source.icon;
        return (
          <div
            key={source.name}
            className="bg-graphite border border-slate-dark p-4 hover:border-gold/50 transition-colors"
          >
            <div className="flex items-center gap-3 mb-2">
              <IconComponent className="w-5 h-5 text-gold" />
              <span className="text-ivory font-medium">{source.name}</span>
            </div>
            <p className="text-stone-500 text-sm leading-relaxed">{source.description}</p>
          </div>
        );
      })}
    </div>
  );
}

export default MethodologySection;
